import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import fetchAPI from '../services/api';

const STATUS_COLORS = {
  draft: 'text-gray-400 bg-gray-500/10',
  running: 'text-emerald-400 bg-emerald-500/10',
  paused: 'text-amber-400 bg-amber-500/10',
  completed: 'text-blue-400 bg-blue-500/10',
  failed: 'text-red-400 bg-red-500/10',
};

const OUTCOME_COLORS = {
  improved: 'text-emerald-400',
  regressed: 'text-red-400',
  neutral: 'text-gray-400',
  crash: 'text-red-500',
};

const TYPE_LABELS = {
  strategy: 'Strategy Optimization',
  research: 'Commerce Research',
  integration: 'Integration Testing',
};

const METRIC_OPTIONS = [
  { value: 'net_usdc_earned', label: 'Net USDC Earned' },
  { value: 'approval_rate', label: 'Approval Rate' },
  { value: 'deal_velocity', label: 'Deal Velocity' },
  { value: 'deal_close_rate', label: 'Deal Close Rate' },
  { value: 'research_quality_score', label: 'Research Quality Score' },
  { value: 'success_rate', label: 'Success Rate' },
  { value: 'settlement_time_seconds', label: 'Settlement Time' },
  { value: 'a2a_deal_revenue', label: 'A2A Deal Revenue' },
];

function StatusBadge({ status }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status] || STATUS_COLORS.draft}`}>
      {status}
    </span>
  );
}

// ──────────────────────────────────────────────
// Metrics Sparkline (SVG)
// ──────────────────────────────────────────────
function Sparkline({ runs, metricKey = 'primary_value' }) {
  if (!runs || runs.length < 2) return null;

  const values = runs.map(r => r.metrics?.[metricKey] ?? r.metrics?.primary_value ?? 0).reverse();
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const w = 200;
  const h = 40;
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y = h - ((v - min) / range) * (h - 4) - 2;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={w} height={h} className="mt-1">
      <polyline fill="none" stroke="#34d399" strokeWidth="1.5" points={points} />
      {values.map((v, i) => {
        const x = (i / (values.length - 1)) * w;
        const y = h - ((v - min) / range) * (h - 4) - 2;
        return <circle key={i} cx={x} cy={y} r="2" fill="#34d399" />;
      })}
    </svg>
  );
}

// ──────────────────────────────────────────────
// Setup Wizard
// ──────────────────────────────────────────────
function SetupWizard({ apiKey, onCreated, onCancel }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: '',
    experiment_type: 'strategy',
    objective: '',
    primary_metric: 'net_usdc_earned',
    metric_direction: 'higher_is_better',
    max_usdc_per_run: 100,
    max_runs: 50,
    stop_if_no_improvement: 10,
    time_window_minutes: 60,
    sandbox_mode: false,
    instructions: '',
    // Variant space
    bid_min: 5, bid_max: 100,
    accept_below_min: 10, accept_below_max: 500,
    step_down_min: 0.5, step_down_max: 10,
    task_types: ['fetch_deals', 'price_watch', 'research'],
  });
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const body = {
        name: form.name,
        experiment_type: form.experiment_type,
        program: {
          objective: form.objective,
          primary_metric: form.primary_metric,
          metric_direction: form.metric_direction,
          constraints: {
            max_usdc_per_run: form.max_usdc_per_run,
            max_runs: form.max_runs,
            stop_if_no_improvement: form.stop_if_no_improvement,
            time_window_minutes: form.time_window_minutes,
            sandbox_mode: form.sandbox_mode,
          },
          variant_space: {
            bid_range: { min: form.bid_min, max: form.bid_max },
            accept_below_range: { min: form.accept_below_min, max: form.accept_below_max },
            step_down_range: { min: form.step_down_min, max: form.step_down_max },
            task_types: form.task_types,
          },
          instructions: form.instructions,
        },
      };
      const result = await fetchAPI('/api/v1/experiments', {
        method: 'POST',
        headers: { Authorization: `Bearer ${apiKey}` },
        body: JSON.stringify(body),
      });
      onCreated(result);
    } catch (err) {
      alert('Error creating experiment: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  const inputClass = "w-full bg-[hsl(var(--background))] border border-[hsl(var(--border))] rounded-lg px-3 py-2 text-sm text-[hsl(var(--foreground))] focus:outline-none focus:border-emerald-500";
  const labelClass = "block text-sm text-[hsl(var(--muted-foreground))] mb-1";

  return (
    <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/50 p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold">New Experiment</h2>
        <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
          {[1,2,3,4,5].map(s => (
            <span key={s} className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${s === step ? 'bg-emerald-500 text-white' : s < step ? 'bg-emerald-500/20 text-emerald-400' : 'bg-[hsl(var(--secondary))]/30'}`}>
              {s}
            </span>
          ))}
        </div>
      </div>

      {step === 1 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Basics</h3>
          <div>
            <label className={labelClass}>Experiment Name</label>
            <input className={inputClass} value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., Optimize A2A negotiation thresholds" />
          </div>
          <div>
            <label className={labelClass}>Type</label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(TYPE_LABELS).map(([val, label]) => (
                <button key={val} onClick={() => setForm({...form, experiment_type: val})}
                  className={`p-3 rounded-lg border text-sm text-left ${form.experiment_type === val ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-emerald-500/30'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={labelClass}>Objective</label>
            <textarea className={inputClass} rows={3} value={form.objective} onChange={e => setForm({...form, objective: e.target.value})} placeholder="What are you trying to optimize? e.g., Maximize net earnings while maintaining 80%+ approval rate" />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Primary Metric</h3>
          <div>
            <label className={labelClass}>Metric to Optimize</label>
            <select className={inputClass} value={form.primary_metric} onChange={e => setForm({...form, primary_metric: e.target.value})}>
              {METRIC_OPTIONS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Direction</label>
            <div className="grid grid-cols-2 gap-2">
              {[['higher_is_better', 'Higher is Better'], ['lower_is_better', 'Lower is Better']].map(([val, label]) => (
                <button key={val} onClick={() => setForm({...form, metric_direction: val})}
                  className={`p-3 rounded-lg border text-sm ${form.metric_direction === val ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Constraints</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Budget per Run (USDC)</label>
              <input type="number" className={inputClass} value={form.max_usdc_per_run} onChange={e => setForm({...form, max_usdc_per_run: Number(e.target.value)})} />
            </div>
            <div>
              <label className={labelClass}>Max Runs</label>
              <input type="number" className={inputClass} value={form.max_runs} onChange={e => setForm({...form, max_runs: Number(e.target.value)})} />
            </div>
            <div>
              <label className={labelClass}>Stop if No Improvement (runs)</label>
              <input type="number" className={inputClass} value={form.stop_if_no_improvement} onChange={e => setForm({...form, stop_if_no_improvement: Number(e.target.value)})} />
            </div>
            <div>
              <label className={labelClass}>Time Window (minutes)</label>
              <input type="number" className={inputClass} value={form.time_window_minutes} onChange={e => setForm({...form, time_window_minutes: Number(e.target.value)})} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="sandbox" checked={form.sandbox_mode} onChange={e => setForm({...form, sandbox_mode: e.target.checked})} className="rounded" />
            <label htmlFor="sandbox" className="text-sm text-[hsl(var(--muted-foreground))]">Sandbox Mode (no real USDC movement)</label>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Variant Space</h3>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Define the ranges your agent can vary during experiments.</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Bid Range (USDC)</label>
              <div className="flex gap-2">
                <input type="number" className={inputClass} placeholder="Min" value={form.bid_min} onChange={e => setForm({...form, bid_min: Number(e.target.value)})} />
                <input type="number" className={inputClass} placeholder="Max" value={form.bid_max} onChange={e => setForm({...form, bid_max: Number(e.target.value)})} />
              </div>
            </div>
            <div>
              <label className={labelClass}>Accept Below (USDC)</label>
              <div className="flex gap-2">
                <input type="number" className={inputClass} placeholder="Min" value={form.accept_below_min} onChange={e => setForm({...form, accept_below_min: Number(e.target.value)})} />
                <input type="number" className={inputClass} placeholder="Max" value={form.accept_below_max} onChange={e => setForm({...form, accept_below_max: Number(e.target.value)})} />
              </div>
            </div>
            <div>
              <label className={labelClass}>Step Down (USDC)</label>
              <div className="flex gap-2">
                <input type="number" className={inputClass} placeholder="Min" value={form.step_down_min} onChange={e => setForm({...form, step_down_min: Number(e.target.value)})} step="0.5" />
                <input type="number" className={inputClass} placeholder="Max" value={form.step_down_max} onChange={e => setForm({...form, step_down_max: Number(e.target.value)})} step="0.5" />
              </div>
            </div>
          </div>
        </div>
      )}

      {step === 5 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Instructions (program.md)</h3>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Free-text guidance for your agent. Think of this as autoresearch's program.md — tell your agent what strategies to try, what to avoid, and how to think about the results.</p>
          <textarea className={`${inputClass} font-mono`} rows={10} value={form.instructions} onChange={e => setForm({...form, instructions: e.target.value})}
            placeholder={`You are an autonomous commerce agent running experiments to optimize your deal strategy.\n\nRules:\n- Try one change per run\n- If a run improves the metric, keep the change\n- If it regresses, revert and try something else\n- Focus on task types where your approval rate is highest\n- Don't exceed the budget per run\n\nIdeas to try:\n- Lower accept_below threshold to close more deals\n- Focus on higher-bounty tasks\n- Try different task type combinations`} />
        </div>
      )}

      <div className="flex justify-between mt-6">
        <button onClick={step === 1 ? onCancel : () => setStep(step - 1)} className="px-4 py-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
          {step === 1 ? 'Cancel' : 'Back'}
        </button>
        {step < 5 ? (
          <button onClick={() => setStep(step + 1)} disabled={step === 1 && !form.name}
            className="px-4 py-2 bg-emerald-500 text-white text-sm rounded-lg hover:bg-emerald-600 disabled:opacity-50">
            Next
          </button>
        ) : (
          <button onClick={handleCreate} disabled={creating || !form.name}
            className="px-4 py-2 bg-emerald-500 text-white text-sm rounded-lg hover:bg-emerald-600 disabled:opacity-50">
            {creating ? 'Creating...' : 'Create Experiment'}
          </button>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// Experiment Detail View
// ──────────────────────────────────────────────
function ExperimentDetail({ experiment, runs, apiKey, onBack, onRefresh }) {
  const [acting, setActing] = useState(false);
  const exp = experiment;

  const handleAction = async (action) => {
    setActing(true);
    try {
      await fetchAPI(`/api/v1/experiments/${exp.experiment_id}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${apiKey}` },
      });
      onRefresh();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setActing(false);
    }
  };

  const program = exp.program || {};
  const constraints = program.constraints || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button onClick={onBack} className="text-sm text-[hsl(var(--muted-foreground))] hover:text-emerald-400 mb-2">&larr; Back to Experiments</button>
          <h2 className="text-2xl font-bold">{exp.name}</h2>
          <div className="flex items-center gap-3 mt-2">
            <StatusBadge status={exp.status} />
            <span className="text-sm text-[hsl(var(--muted-foreground))]">{TYPE_LABELS[exp.experiment_type]}</span>
            <span className="text-sm text-[hsl(var(--muted-foreground))]">Run {exp.total_runs || 0}{constraints.max_runs ? ` / ${constraints.max_runs}` : ''}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {exp.status === 'draft' && <button onClick={() => handleAction('start')} disabled={acting} className="px-3 py-1.5 bg-emerald-500 text-white text-sm rounded-lg hover:bg-emerald-600 disabled:opacity-50">Start</button>}
          {exp.status === 'running' && <button onClick={() => handleAction('pause')} disabled={acting} className="px-3 py-1.5 bg-amber-500 text-white text-sm rounded-lg hover:bg-amber-600 disabled:opacity-50">Pause</button>}
          {exp.status === 'paused' && <button onClick={() => handleAction('resume')} disabled={acting} className="px-3 py-1.5 bg-emerald-500 text-white text-sm rounded-lg hover:bg-emerald-600 disabled:opacity-50">Resume</button>}
          {exp.best_strategy && <button onClick={() => handleAction('adopt')} disabled={acting} className="px-3 py-1.5 border border-emerald-500 text-emerald-400 text-sm rounded-lg hover:bg-emerald-500/10 disabled:opacity-50">Adopt Best</button>}
        </div>
      </div>

      {/* Program Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/50 p-4">
          <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">Objective</div>
          <div className="text-sm">{program.objective || 'Not set'}</div>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/50 p-4">
          <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">Primary Metric</div>
          <div className="text-sm font-medium text-emerald-400">{program.primary_metric}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))]">{program.metric_direction === 'higher_is_better' ? 'Higher is better' : 'Lower is better'}</div>
        </div>
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/50 p-4">
          <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">Constraints</div>
          <div className="text-xs space-y-0.5">
            <div>Budget: <strong>${constraints.max_usdc_per_run}/run</strong></div>
            <div>Window: <strong>{constraints.time_window_minutes}min</strong></div>
            <div>Stop: <strong>{constraints.stop_if_no_improvement} non-improvements</strong></div>
            {constraints.sandbox_mode && <div className="text-amber-400">Sandbox Mode</div>}
          </div>
        </div>
      </div>

      {/* Best vs Baseline */}
      {exp.best_metrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
            <div className="text-xs text-emerald-400 mb-2 font-medium">Best Performance</div>
            <div className="text-2xl font-bold text-emerald-400">{exp.best_metrics.primary_value?.toFixed?.(2) ?? '—'}</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">in {exp.successful_runs || 0} improved runs</div>
          </div>
          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/50 p-4">
            <div className="text-xs text-[hsl(var(--muted-foreground))] mb-2">Baseline</div>
            <div className="text-2xl font-bold">{exp.baseline_metrics?.primary_value?.toFixed?.(2) ?? '—'}</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">captured at experiment start</div>
          </div>
        </div>
      )}

      {/* Metrics Chart */}
      {runs.length > 1 && (
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/50 p-4">
          <div className="text-sm font-medium mb-2">Metric Trend</div>
          <Sparkline runs={runs} />
        </div>
      )}

      {/* Best Strategy */}
      {exp.best_strategy && (
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/50 p-4">
          <div className="text-sm font-medium mb-2">Best Strategy</div>
          <pre className="text-xs text-emerald-400 bg-[hsl(var(--background))] rounded-lg p-3 overflow-x-auto">
            {JSON.stringify(exp.best_strategy, null, 2)}
          </pre>
        </div>
      )}

      {/* Run History */}
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/50 p-4">
        <div className="text-sm font-medium mb-3">Run History</div>
        {runs.length === 0 ? (
          <div className="text-sm text-[hsl(var(--muted-foreground))] text-center py-6">No runs yet. Start the experiment and let your agent report results.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[hsl(var(--muted-foreground))] text-xs">
                  <th className="text-left py-2 pr-4">#</th>
                  <th className="text-left py-2 pr-4">Outcome</th>
                  <th className="text-right py-2 pr-4">Primary</th>
                  <th className="text-right py-2 pr-4">vs Baseline</th>
                  <th className="text-right py-2 pr-4">Duration</th>
                  <th className="text-left py-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {runs.map(run => (
                  <tr key={run.run_id} className="border-t border-[hsl(var(--border))]/50">
                    <td className="py-2 pr-4 font-mono">{run.run_number}</td>
                    <td className={`py-2 pr-4 font-medium ${OUTCOME_COLORS[run.outcome] || ''}`}>{run.outcome}</td>
                    <td className="py-2 pr-4 text-right font-mono">{run.metrics?.primary_value?.toFixed?.(2) ?? '—'}</td>
                    <td className="py-2 pr-4 text-right font-mono">
                      <span className={run.delta_vs_baseline > 0 ? 'text-emerald-400' : run.delta_vs_baseline < 0 ? 'text-red-400' : ''}>
                        {run.delta_vs_baseline > 0 ? '+' : ''}{(run.delta_vs_baseline * 100).toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-right text-[hsl(var(--muted-foreground))]">{Math.round(run.duration_seconds || 0)}s</td>
                    <td className="py-2 text-[hsl(var(--muted-foreground))] truncate max-w-[200px]">{run.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Instructions (program.md) */}
      {program.instructions && (
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/50 p-4">
          <div className="text-sm font-medium mb-2">Agent Instructions (program.md)</div>
          <pre className="text-xs text-[hsl(var(--muted-foreground))] bg-[hsl(var(--background))] rounded-lg p-3 whitespace-pre-wrap">{program.instructions}</pre>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────
// Main Page
// ──────────────────────────────────────────────
export default function ExperimentsPage() {
  const [apiKey, setApiKey] = useState(() => sessionStorage.getItem('ci_api_key') || '');
  const [keyInput, setKeyInput] = useState(apiKey);
  const [connected, setConnected] = useState(!!apiKey);
  const [experiments, setExperiments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState('list'); // list | wizard | detail
  const [selectedExp, setSelectedExp] = useState(null);
  const [selectedRuns, setSelectedRuns] = useState([]);

  const fetchExperiments = useCallback(async () => {
    if (!apiKey) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchAPI('/api/v1/experiments', {
        headers: { Authorization: `Bearer ${apiKey}` },
      });
      setExperiments(data.experiments || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiKey]);

  const fetchExperimentDetail = useCallback(async (experimentId) => {
    try {
      const data = await fetchAPI(`/api/v1/experiments/${experimentId}`, {
        headers: { Authorization: `Bearer ${apiKey}` },
      });
      setSelectedExp(data.experiment);
      setSelectedRuns(data.recent_runs || []);
      setView('detail');
    } catch (err) {
      setError(err.message);
    }
  }, [apiKey]);

  useEffect(() => {
    if (connected && apiKey) fetchExperiments();
  }, [connected, apiKey, fetchExperiments]);

  const handleConnect = () => {
    if (keyInput) {
      sessionStorage.setItem('ci_api_key', keyInput);
      setApiKey(keyInput);
      setConnected(true);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Experiments</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
              Autonomous experiment loops for commerce strategy optimization
            </p>
          </div>
        </div>

        {/* API Key Input */}
        {!connected && (
          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/50 p-6 text-center">
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">Enter your API key to manage experiments</p>
            <div className="flex gap-2 max-w-md mx-auto">
              <input
                type="password"
                value={keyInput}
                onChange={e => setKeyInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleConnect()}
                placeholder="ci_ai_live_..."
                className="flex-1 bg-[hsl(var(--background))] border border-[hsl(var(--border))] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
              />
              <button onClick={handleConnect} className="px-4 py-2 bg-emerald-500 text-white text-sm rounded-lg hover:bg-emerald-600">
                Connect
              </button>
            </div>
          </div>
        )}

        {error && <div className="text-red-400 text-sm mb-4">{error}</div>}

        {/* Connected Views */}
        {connected && view === 'list' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm text-[hsl(var(--muted-foreground))]">{experiments.length} experiment{experiments.length !== 1 ? 's' : ''}</span>
              <button onClick={() => setView('wizard')} className="px-4 py-2 bg-emerald-500 text-white text-sm rounded-lg hover:bg-emerald-600">
                New Experiment
              </button>
            </div>

            {loading && <div className="text-center py-12 text-[hsl(var(--muted-foreground))]">Loading...</div>}

            {!loading && experiments.length === 0 && (
              <div className="text-center py-16">
                <div className="text-4xl mb-4">&#x1F9EA;</div>
                <p className="text-lg font-medium mb-2">No experiments yet</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-md mx-auto">
                  Create your first experiment to start optimizing your agent's commerce strategy.
                  Like autoresearch's program.md, you write the program — your agent runs the loop.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {experiments.map(exp => (
                <button key={exp.experiment_id} onClick={() => fetchExperimentDetail(exp.experiment_id)}
                  className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/50 p-4 text-left hover:border-emerald-500/30 transition-colors">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium truncate mr-2">{exp.name}</h3>
                    <StatusBadge status={exp.status} />
                  </div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] mb-3">{TYPE_LABELS[exp.experiment_type]}</div>
                  <div className="flex items-center gap-4 text-xs text-[hsl(var(--muted-foreground))]">
                    <span>{exp.total_runs || 0} runs</span>
                    <span>{exp.successful_runs || 0} improved</span>
                    {exp.best_metrics?.primary_value != null && (
                      <span className="text-emerald-400">Best: {exp.best_metrics.primary_value.toFixed?.(2)}</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {connected && view === 'wizard' && (
          <SetupWizard
            apiKey={apiKey}
            onCreated={(result) => {
              fetchExperiments();
              setView('list');
            }}
            onCancel={() => setView('list')}
          />
        )}

        {connected && view === 'detail' && selectedExp && (
          <ExperimentDetail
            experiment={selectedExp}
            runs={selectedRuns}
            apiKey={apiKey}
            onBack={() => { setView('list'); fetchExperiments(); }}
            onRefresh={() => fetchExperimentDetail(selectedExp.experiment_id)}
          />
        )}
      </main>
    </div>
  );
}
